import { useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import BloodtypeOutlinedIcon from "@mui/icons-material/BloodtypeOutlined";
import OpacityOutlinedIcon from "@mui/icons-material/OpacityOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EducationShell from "../../components/education/EducationShell";

// ─── Palette — matches EducationLanding.jsx / TypesLanding.jsx ───────────────
const INK = "#0F172A";
const SLATE = "#475569";
const MUTED = "#64748B";
const BORDER = "#E2E8F0";
const ACCENT = "#0F766E";
const ACCENT_SOFT = "#F0FDFA";
const PAPER = "#FFFFFF";
const CANVAS = "#F8FAFC";

const HOW_IT_WORKS = [
  { step: "01", title: "Nephrons filter blood", detail: "Each kidney has about a million nephrons — tiny filtering units that remove waste and excess fluid from the blood." },
  { step: "02", title: "Damage accumulates", detail: "Diabetes, high blood pressure, immune attack, or genetic cysts gradually damage nephrons over months to years." },
  { step: "03", title: "Filtering capacity drops", detail: "As nephrons are lost, eGFR declines and waste products, fluid, and electrolytes that should be filtered out start to build up." },
  { step: "04", title: "Complications develop", detail: "Reduced filtering affects far more than the kidneys — it disrupts the heart, bones, blood, nerves, and GI tract too." },
];

const TYPES_SUMMARY = [
  { code: "DN", path: "/education/types/diabetic", name: "Diabetic Nephropathy", note: "Most common cause of CKD; long-term high blood sugar damages the kidneys." },
  { code: "HN", path: "/education/types/hypertensive", name: "Hypertensive Nephrosclerosis", note: "Second most common cause; long-standing high blood pressure damages the kidneys." },
  { code: "GN", path: "/education/types/glomerulonephritis", name: "Glomerulonephritis", note: "Immune-mediated inflammation of the kidneys' filtering units." },
  { code: "PKD", path: "/education/types/pkd", name: "Polycystic Kidney Disease", note: "Hereditary; cysts gradually enlarge the kidneys and impair function." },
  { code: "Other", path: "/education/types/other", name: "Other & Unknown", note: "Obstruction, infection, medications, or no clear identified cause." },
];

const COMPLICATIONS = [
  { label: "Cardiovascular", path: "/education/basics/cardiovascular", icon: FavoriteBorderOutlinedIcon, note: "The leading cause of death in CKD patients — hypertension, heart failure, and coronary disease risk all rise together with CKD." },
  { label: "Neurological", path: "/education/basics/neuro", icon: PsychologyOutlinedIcon, note: "Uremic peripheral neuropathy and restless leg syndrome, more common as CKD advances." },
  { label: "Gastrointestinal", path: "/education/basics/gi", icon: RestaurantOutlinedIcon, note: "Nausea, appetite loss, and taste changes from uremic toxin buildup." },
  { label: "Bone & Mineral", path: "/education/basics/bone-mineral", icon: AccessibilityNewOutlinedIcon, note: "CKD-MBD — disrupted calcium, phosphorus, and PTH balance affecting bone strength." },
  { label: "Hematologic", path: "/education/basics/anemia", icon: BloodtypeOutlinedIcon, note: "Anemia from reduced erythropoietin production — the most common blood-related complication." },
  { label: "Fluid & Electrolyte", path: "/education/basics/fluid-electrolyte", icon: OpacityOutlinedIcon, note: "Fluid retention and hyperkalemia — the two complications that need the most prompt attention." },
];

const POPULATIONS = [
  { type: "Diabetic Nephropathy", age: "Usually after 10–15 years of diabetes", who: "Risk rises with poor glycemic control and coexisting hypertension; the leading cause of CKD in the U.S." },
  { type: "Hypertensive Nephrosclerosis", age: "Middle-aged and older adults", who: "Disproportionately affects Black patients, who tend to develop it earlier and progress faster." },
  { type: "Glomerulonephritis", age: "Varies widely by subtype", who: "IgA nephropathy is most common in young adults; lupus nephritis is more common in women of childbearing age, especially Black and Hispanic women." },
  { type: "Polycystic Kidney Disease", age: "Often diagnosed by 30s–50s", who: "Hereditary — a parent with ADPKD means each child has a 50% inheritance risk, regardless of background." },
  { type: "Other & Unknown", age: "Variable", who: "Determined more by the specific underlying cause (obstruction, infection, medications) than by demographics." },
];

export default function CKDComplicationsHub() {
  const navigate = useNavigate();
  const [hoveredComplication, setHoveredComplication] = useState(null);

  return (
    <EducationShell breadcrumbs={[{ label: "Learn", path: "/education" }, { label: "CKD Complications" }]}>
      <Box sx={{ fontFamily: "'DM Sans', sans-serif", bgcolor: PAPER }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:ital,wght@0,300;0,600;1,300&display=swap');
        `}</style>

        {/* ── Intro ── */}
        <Box sx={{ px: { xs: 3, md: 8 }, pt: { xs: 5, md: 7 }, pb: { xs: 5, md: 6 }, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT, mb: 2 }}>
            CKD Complications
          </Typography>
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.8rem", md: "2.4rem" }, fontWeight: 600, lineHeight: 1.2, color: INK, mb: 2.5, maxWidth: 700 }}>
            An overview — what CKD is, who it affects, and what it touches beyond the kidneys
          </Typography>
          <Typography sx={{ fontSize: "0.95rem", color: SLATE, maxWidth: 700, lineHeight: 1.8 }}>
            Chronic kidney disease is a gradual decline in the kidneys' ability to filter blood. It's not one
            disease — the underlying cause determines how it's monitored and treated, and as it progresses it
            can affect far more than the kidneys themselves. This page gives a broad overview; each section
            links out to a deeper, dedicated page.
          </Typography>
        </Box>

        {/* ── How it works ── */}
        <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 6, md: 7 }, bgcolor: CANVAS }}>
          <SectionLabel>The mechanism</SectionLabel>
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.5rem", md: "1.9rem" }, fontWeight: 600, color: INK, mb: 5 }}>
            How CKD develops and progresses
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4,1fr)" }, gap: 3 }}>
            {HOW_IT_WORKS.map((step, i) => (
              <Box key={i} sx={{ borderTop: `2px solid ${ACCENT}`, pt: 2.5 }}>
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", color: ACCENT, mb: 1 }}>
                  {step.step}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: INK, mb: 1 }}>
                  {step.title}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: SLATE, lineHeight: 1.65 }}>
                  {step.detail}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Causes overview ── */}
        <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 6, md: 7 } }}>
          <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 5 }}>
            <Box>
              <SectionLabel>What are the causes</SectionLabel>
              <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.5rem", md: "1.9rem" }, fontWeight: 600, color: INK }}>
                Five main causes
              </Typography>
            </Box>
            <Button
              onClick={() => navigate("/education/types")}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
              sx={{ fontWeight: 600, fontSize: "0.85rem", color: ACCENT, textTransform: "none" }}
            >
              See full detail
            </Button>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5,1fr)" }, gap: 2 }}>
            {TYPES_SUMMARY.map((t) => (
              <Box
                key={t.code}
                onClick={() => navigate(t.path)}
                sx={{ border: `1px solid ${BORDER}`, borderRadius: "10px", p: 2.5, cursor: "pointer", "&:hover": { borderColor: ACCENT } }}
              >
                <Box sx={{ display: "inline-flex", px: 1.25, py: 0.4, borderRadius: "6px", bgcolor: ACCENT_SOFT, color: ACCENT, fontSize: "0.7rem", fontWeight: 700, mb: 1.5 }}>
                  {t.code}
                </Box>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: INK, mb: 0.75 }}>
                  {t.name}
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: SLATE, lineHeight: 1.55 }}>
                  {t.note}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Complications ── */}
        <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 6, md: 8 }, bgcolor: CANVAS }}>
          <SectionLabel>What it affects beyond the kidneys</SectionLabel>
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.5rem", md: "1.9rem" }, fontWeight: 600, color: INK, mb: 2 }}>
            Six complications to know about
          </Typography>
          <Typography sx={{ fontSize: "0.92rem", color: SLATE, maxWidth: 640, lineHeight: 1.75, mb: 5 }}>
            As CKD progresses, reduced kidney function affects the rest of the body too. Which complications
            show up, and how severely, varies by cause and stage. Select one to learn more.
          </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 2.5 }}>
            {COMPLICATIONS.map((c) => {
              const Icon = c.icon;
              const isHovered = hoveredComplication === c.label;
              return (
                <Box
                  key={c.label}
                  onClick={() => navigate(c.path)}
                  onMouseEnter={() => setHoveredComplication(c.label)}
                  onMouseLeave={() => setHoveredComplication(null)}
                  sx={{
                    border: `1px solid ${isHovered ? ACCENT : BORDER}`,
                    borderRadius: "10px",
                    bgcolor: PAPER,
                    p: 3,
                    cursor: "pointer",
                    transition: "border-color 0.15s ease",
                  }}
                >
                  <Box sx={{ width: 38, height: 38, borderRadius: "8px", bgcolor: ACCENT_SOFT, display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                    <Icon sx={{ fontSize: 19, color: ACCENT }} />
                  </Box>
                  <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: INK, mb: 1 }}>
                    {c.label}
                  </Typography>
                  <Typography sx={{ fontSize: "0.82rem", color: SLATE, lineHeight: 1.6 }}>
                    {c.note}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ── Populations & age groups ── */}
        <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 6, md: 8 } }}>
          <SectionLabel>Who it affects</SectionLabel>
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.5rem", md: "1.9rem" }, fontWeight: 600, color: INK, mb: 2 }}>
            Populations and age groups most impacted
          </Typography>
          <Typography sx={{ fontSize: "0.92rem", color: SLATE, maxWidth: 640, lineHeight: 1.75, mb: 5 }}>
            Typical onset and affected populations vary meaningfully by underlying cause — useful context for
            recognizing CKD risk earlier.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {POPULATIONS.map((p) => (
              <Box
                key={p.type}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "220px 200px 1fr" },
                  gap: { xs: 1, sm: 3 },
                  border: `1px solid ${BORDER}`,
                  borderRadius: "8px",
                  p: 2.5,
                  alignItems: "start",
                }}
              >
                <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: INK }}>{p.type}</Typography>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: ACCENT }}>{p.age}</Typography>
                <Typography sx={{ fontSize: "0.85rem", color: SLATE, lineHeight: 1.65 }}>{p.who}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── CTA ── */}
        <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 7, md: 9 }, bgcolor: CANVAS }}>
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: "12px", bgcolor: PAPER, p: { xs: 4, md: 6 }, maxWidth: 760, mx: "auto", textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT, mb: 2 }}>
              Ready to take action?
            </Typography>
            <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.5rem", md: "1.9rem" }, fontWeight: 600, color: INK, mb: 2 }}>
              Find clinical trials matched to your cause
            </Typography>
            <Typography sx={{ fontSize: "0.92rem", color: SLATE, lineHeight: 1.75, mb: 4, maxWidth: 560, mx: "auto" }}>
              NephroTrax uses your CKD stage, etiology, and location to match you to open clinical trials —
              including travel coordination for trials far from home.
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                onClick={() => navigate("/trials/browse")}
                endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                sx={{ fontWeight: 500, fontSize: "0.92rem", px: 3.25, py: 1.4, bgcolor: "#A7F3D0", color: "#065F46", borderRadius: "6px", textTransform: "none", "&:hover": { bgcolor: "#065F46", color: "#FFFFFF" } }}
              >
                Find my trials
              </Button>
              <Button
                onClick={() => navigate("/education/treatment")}
                sx={{ fontWeight: 500, fontSize: "0.92rem", px: 3.25, py: 1.4, bgcolor: "#A7F3D0", color: "#065F46", border: "1px solid #A7F3D0", borderRadius: "6px", textTransform: "none", "&:hover": { bgcolor: "#065F46", color: "#FFFFFF", borderColor: "#065F46" } }}
              >
                View treatment options
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </EducationShell>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: ACCENT, mb: 2 }}>
      {children}
    </Typography>
  );
}